// SignIn.js
import React, { useState, useContext, useEffect } from 'react';
import {
  Typography,
  Button,
  Box,
  Container,
  Paper,
  Link,
  CircularProgress,
  TextField,
  Alert,
  Snackbar,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { auth } from '../firebase';
import {
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Logo from '../mind-engage-blue-logo.png';
import GoogleIcon from '@mui/icons-material/Google';
import { AuthContext } from '../context/AuthContext';

const GradientBackground = styled(Box)(() => ({
  minHeight: '100vh',
  width: '100vw',
  background: 'radial-gradient(circle at 40% 20%, #8BCDFF 0%, #B9DCFF 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  position: 'relative',
}));

function SignIn() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const {
    user,
    sendMagicLink,
    pendingLink,
    completeLinkSignIn,
    error: linkError,
    clearPendingLink,
    clearError,
  } = useContext(AuthContext);

  // Redirect already-authenticated users
  useEffect(() => {
    if (user && !pendingLink) {
      navigate('/generate', { replace: true });
    }
  }, [user, pendingLink, navigate]);

  // Prefill the confirmation dialog when a pending link arrives
  const [confirmEmail, setConfirmEmail] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  useEffect(() => {
    if (pendingLink) {
      const stored = window.localStorage.getItem('magicEmail');
      if (stored) setConfirmEmail(stored);
    }
  }, [pendingLink]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      navigate('/generate');
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setErrorMsg(error.message || 'Error signing in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    setBusy(true);
    try {
      await sendMagicLink(email);
      setSnack({
        open: true,
        message: 'We have e-mailed you a sign-in link. Check your inbox!',
        severity: 'success',
      });
      setShowEmailForm(false);
      setEmail('');
      setErrorMsg('');
    } catch (e) {
      console.error(e);
      setErrorMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    setConfirmBusy(true);
    try {
      await completeLinkSignIn(confirmEmail);
    } finally {
      setConfirmBusy(false);
    }
  };

  return (
    <GradientBackground>
      <Container maxWidth="sm" sx={{ zIndex: 1 }}>
        <Paper
          elevation={6}
          role="banner"
          aria-label="MindEngage Authoring Studio sign-in"
          sx={{
            p: 4,
            textAlign: 'center',
            width: 'clamp(320px, 50vw, 480px)',
            borderRadius: '20px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
          }}
        >
          <Box sx={{ mb: 4, mt: -1 }}>
            <Box
              component="img"
              src={Logo}
              alt="MindEngage logo"
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.12))',
              }}
            />
          </Box>
          <Typography
            variant="h3"
            gutterBottom
            sx={{ color: '#5F4DFE', lineHeight: 1.2 }}
          >
            <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>
              MindEngage
            </Box>
            <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
              Authoring Studio
            </Box>
          </Typography>
          <Typography
            variant="subtitle1"
            paragraph
            sx={{ fontSize: '1rem', maxWidth: '28ch', mx: 'auto' }}
          >
            Generate interactive lessons & publish to Classroom in minutes.
          </Typography>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}

          {/* Google & E-mail buttons */}
          {!showEmailForm && (
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
              }}
            >
              <Button
                variant="contained"
                onClick={handleGoogleSignIn}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <GoogleIcon />
                  )
                }
                sx={{
                  backgroundColor: '#4285F4',
                  color: '#fff',
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  letterSpacing: '.25px',
                  '&:hover': {
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                    backgroundColor: '#357ae8',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #5F4DFE',
                    outlineOffset: '2px',
                  },
                }}
                aria-label="Continue with Google account"
              >
                Continue with Google
              </Button>
              <Button
                variant="outlined"
                sx={{
                  fontSize: '1rem',
                  fontWeight: 500,
                  letterSpacing: '.25px',
                  textTransform: 'none' 
                }}
                onClick={() => setShowEmailForm(true)}
                disabled={loading}
              >
                Continue with Email
              </Button>
            </Box>
          )}

          {/* Magic-link section */}
          <Collapse in={showEmailForm} unmountOnExit>
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              <TextField
                label="E-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  justifyContent: 'center',
                  mt: 1,
                }}
              >
                <Button onClick={handleMagicLink} disabled={busy}
                                sx={{
                                  fontSize: '1rem',
                                  fontWeight: 500,
                                  letterSpacing: '.25px',
                                  textTransform: 'none' 
                                }}>
                Email Sign-In Link
                </Button>
                <Button
                  onClick={() => {
                    setShowEmailForm(false);
                    setEmail('');
                    setErrorMsg('');
                  }}
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '.25px',
                    textTransform: 'none' 
                  }}
                  disabled={busy}
                >
                  Back
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Paper>
      </Container>

      {/* Footer unchanged */}
      <Box
        component="footer"
        sx={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          py: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          textAlign: 'center',
          opacity: 0.8,
        }}
      >
        <Typography
          variant="caption"
          component="span"
          sx={{ color: 'text.secondary' }}
        >
          Disclaimer: MindEngage is a Proof of Concept and may not always provide
          accurate information.
        </Typography>
        <Box component="span" sx={{ mx: 1, color: 'text.secondary' }}>
          ·
        </Box>
        <Link
          href="/terms"
          variant="caption"
          sx={{ color: 'text.secondary', mx: 0.5 }}
          target="_blank"
          rel="noopener"
        >
          Terms
        </Link>
        <Link
          href="/privacy"
          variant="caption"
          sx={{ color: 'text.secondary', mx: 0.5 }}
          target="_blank"
          rel="noopener"
        >
          Privacy
        </Link>
      </Box>

      {/* Confirmation dialog for magic-link return stays unchanged */}
      <Dialog open={pendingLink}>
        <DialogTitle>Confirm your e-mail for sign-in</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {linkError && <Alert severity="error">{linkError.message}</Alert>}
          <TextField
            label="E-mail"
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              clearPendingLink();
              clearError();
              navigate('/signin');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={confirmBusy}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </GradientBackground>
  );
}

export default SignIn;
